import { useState, useEffect, useRef } from 'react'
import { X, Download, Upload, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react'
import { renderThumb, canvasToBlob, uploadThumb, thumbFilename } from './thumbRender'

// One game's thumbnail, rendered live from a thumbnail template. Download the
// full 1920x1080 JPEG, or — if the game is already in the catalog — render,
// upload and apply it to that catalog item in one click.
//
// props: season, game, thumbTemplates[], ctxFor(g), sourcesFor(g), api, onApplied(game_id, url), onClose
export default function ThumbPreview({ season, game, thumbTemplates, ctxFor, sourcesFor, api, onApplied, onClose }) {
  const [tplId, setTplId] = useState((thumbTemplates.find(t => t.is_default) || thumbTemplates[0])?.id ?? '')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)   // { kind, text, url? }
  const canvasRef = useRef(null)
  const tpl = thumbTemplates.find(t => String(t.id) === String(tplId)) || null

  useEffect(() => {
    if (!tpl || !canvasRef.current) return
    let cancelled = false
    renderThumb(tpl, { ctx: ctxFor(game), sources: sourcesFor(game) }, { canvas: canvasRef.current, scale: 960 / (tpl.width || 1920) })
      .catch(e => { if (!cancelled) setMsg({ kind: 'err', text: e.message }) })
    return () => { cancelled = true }
  }, [tpl, game, ctxFor, sourcesFor])

  async function fullRender() {
    const c = await renderThumb(tpl, { ctx: ctxFor(game), sources: sourcesFor(game) })
    return canvasToBlob(c)
  }

  async function download() {
    setBusy(true); setMsg(null)
    try {
      const blob = await fullRender()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob); a.download = thumbFilename(season, game); a.click()
      setTimeout(() => URL.revokeObjectURL(a.href), 5000)
    } catch (e) { setMsg({ kind: 'err', text: e.message }) } finally { setBusy(false) }
  }

  async function apply() {
    setBusy(true); setMsg(null)
    try {
      const blob = await fullRender()
      const { url } = await uploadThumb(api.apiRaw, blob, thumbFilename(season, game))
      const r = await api.regenerate(season.id, { items: [{ game_id: game.game_id, thumbnail_url: url }], update_start: false })
      if (r.errors?.length) throw new Error(r.errors[0].error)
      if (!r.updated?.length) throw new Error(r.skipped?.[0]?.reason || 'Nothing was updated')
      setMsg({ kind: 'ok', text: 'Applied to the catalog item.', url })
      onApplied(game.game_id, url)
    } catch (e) { setMsg({ kind: 'err', text: e.body?.error || e.message }) } finally { setBusy(false) }
  }

  return (
    <div className="ts-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose() }}>
      <div className="ts-modal ts-modal-wide" role="dialog" aria-modal="true" aria-labelledby="ts-prev-title">
        <div className="ts-modal-head">
          <h2 id="ts-prev-title">Thumbnail preview <span className="muted">· {game.game_date} · {game.visitor} at {game.home}</span></h2>
          <button type="button" className="ts-iconbtn" onClick={onClose} aria-label="Close" disabled={busy}><X size={16} /></button>
        </div>
        <div className="ts-sch-body">
          {!thumbTemplates.length ? (
            <p className="muted">No thumbnail templates for {season.league} yet — add one under <strong>Thumbnails</strong>.</p>
          ) : (
            <>
              {thumbTemplates.length > 1 && (
                <div className="form-group ts-prev-tpl">
                  <label htmlFor="ts-prev-tplsel">Template</label>
                  <select id="ts-prev-tplsel" value={tplId} onChange={e => setTplId(e.target.value)}>
                    {thumbTemplates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>)}
                  </select>
                </div>
              )}
              <canvas ref={canvasRef} className="ts-prev-canvas" style={{ aspectRatio: `${tpl?.width || 1920} / ${tpl?.height || 1080}` }} aria-label="Rendered thumbnail" />
              {game.tbtv_id && game.thumbnail_url && (
                <p className="muted">Current catalog thumbnail: <a href={game.thumbnail_url} target="_blank" rel="noreferrer">open <ExternalLink size={11} /></a></p>
              )}
              {msg && (
                <p className={`ts-tpl-warn ${msg.kind === 'ok' ? 'is-ok' : ''}`}>
                  {msg.kind === 'ok' ? <CheckCircle size={13} /> : <AlertCircle size={13} />} {msg.text}
                  {msg.url && <> <a href={msg.url} target="_blank" rel="noreferrer">view <ExternalLink size={11} /></a></>}
                </p>
              )}
              <div className="ts-modal-actions">
                <button type="button" onClick={download} disabled={busy || !tpl}><Download size={14} /> Download 1920×1080</button>
                {game.tbtv_id && (
                  <button type="button" onClick={apply} disabled={busy || !tpl} title="Render, upload, and set as this catalog item's thumbnail">
                    <Upload size={14} /> {busy ? 'Applying…' : 'Apply to catalog item'}
                  </button>
                )}
                <span className="ts-spacer" />
                <button type="button" onClick={onClose} disabled={busy}>Close</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
